import { useFeatureFlag } from '../../../../../hooks/FeatureFlagsProvider';

// GrowthBook feature for the Fetch design test (Figma "One-Click Checkout",
// Experiments - Final 10/8/26). Five arms, 20% each, assigned on
// track_trackingNumber. Arms 1.1 and 2.1 were dropped before launch.
export const FETCH_DESIGN_FLAG = 'rm-fetch-design';

export type FetchDesignArm = 'CONTROL' | 'T1_2' | 'T1_3' | 'T2_2' | 'T2_3';

// Treatment 1: photo beside the offer, "For Pet Parents" chip, no bar.
// Treatment 2: the black "For pet parents" bar, photo on the right, Trustpilot.
export type FetchTeaserLayout = 'control' | 't1' | 't2';

export type FetchHero = 'control' | 'catAndDog' | 'dog';

export interface FetchDesign {
  arm: FetchDesignArm;
  layout: FetchTeaserLayout;
  // Which pet photo the offer shows, on the teaser and inside the form.
  hero: FetchHero;
  trustpilot: boolean;
  // The teaser's headline and the line under it. Control keeps today's copy;
  // every treatment shares one new pair, so the copy is constant across them.
  heading: string;
  sub: string;
  // The offer bar on desktop and the collapsed panel on mobile.
  offerLabel: string;
  // The title of the mobile bottom sheet.
  drawerTitle: string;
  // The header of the panel left in the page after the mobile sheet is
  // closed. 1.x shows its label as a chip, so its header stays neutral.
  panelLabel: string;
}

const CONTROL: FetchDesign = {
  arm: 'CONTROL',
  layout: 'control',
  hero: 'control',
  trustpilot: false,
  heading: 'Get paid back up to 90% of vet bills',
  sub: 'Pet insurance that covers dogs & cats nationwide',
  offerLabel: "You've unlocked this offer",
  drawerTitle: 'Special Offer',
  panelLabel: "You've unlocked this offer",
};

const treatment = (
  arm: FetchDesignArm,
  layout: 't1' | 't2',
  hero: FetchHero,
): FetchDesign => ({
  arm,
  layout,
  hero,
  trustpilot: layout === 't2',
  heading: 'Unexpected vet bill? Get up to 90% back.',
  sub: 'Pet insurance for dogs & cats. Use any licensed vet.',
  offerLabel: 'For pet parents',
  drawerTitle: 'For pet parents',
  panelLabel:
    layout === 't2' ? 'For pet parents' : "You've unlocked this offer",
});

const DESIGNS: Record<FetchDesignArm, FetchDesign> = {
  CONTROL,
  T1_2: treatment('T1_2', 't1', 'catAndDog'),
  T1_3: treatment('T1_3', 't1', 'dog'),
  T2_2: treatment('T2_2', 't2', 'catAndDog'),
  T2_3: treatment('T2_3', 't2', 'dog'),
};

// Anything that is not a known arm -- NOT_ENROLLED on retailers outside the
// test, or a typo in GrowthBook -- gets the control design, so a bad flag value
// can never leave the offer half-styled.
export function resolveFetchDesign(value: string | undefined): FetchDesign {
  return value && Object.prototype.hasOwnProperty.call(DESIGNS, value)
    ? DESIGNS[value as FetchDesignArm]
    : CONTROL;
}

export function useFetchDesign(): FetchDesign {
  return resolveFetchDesign(useFeatureFlag(FETCH_DESIGN_FLAG));
}

// What analytics records for a shopper: the arm actually rendered. A value the
// resolver doesn't recognise (a GrowthBook typo) renders control, so it is
// logged as CONTROL rather than as the treatment it was meant to be. Shoppers
// outside the test stay NOT_ENROLLED, which analytics leaves untagged.
export function fetchDesignAnalyticsArm(value: string | undefined): string {
  if (!value || value === 'NOT_ENROLLED') return 'NOT_ENROLLED';
  return resolveFetchDesign(value).arm;
}
